// NobleJobCard - Job Card 2.0 with real-time torque visualization
// Features: Torque gauge, task checklist, time tracking, QR code, signatures
// Perfect for field workers tracking job progress

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Card, Typography, Space, Tag, Checkbox, Button, Progress, Avatar, Divider } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CameraOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  QrcodeOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows } from '../../../theme/noble-tokens';
import { TorqueGauge } from '../NobleProgressRing';
import { NobleButton } from '../NobleButton';

const { Text, Title } = Typography;

export interface JobTask {
  id: string;
  step: number;
  description: string;
  completed: boolean;
  estimatedMinutes?: number;
  actualMinutes?: number;
}

export interface NobleJobCardProps {
  jobNumber: string;
  title: string;
  equipmentId: string;
  equipmentName: string;
  status: 'not-started' | 'in-progress' | 'paused' | 'completed';
  priority: 'normal' | 'urgent' | 'critical';
  torqueValue: number; // Current torque (0-100 or actual Nm)
  maxTorque: number; // Maximum torque capacity
  tasks: JobTask[];
  technician?: {
    name: string;
    avatar?: string;
  };
  location?: string;
  startTime?: Date;
  estimatedDuration?: number; // minutes
  actualDuration?: number; // minutes
  onTaskToggle?: (taskId: string) => void;
  onPause?: () => void;
  onResume?: () => void;
  onComplete?: () => void;
  onAddPhoto?: () => void;
  onViewQR?: () => void;
  className?: string;
}

// Styled Components
const JobCardContainer = styled(Card)`
  border-radius: 16px;
  box-shadow: ${NobleShadows['elevation-2']};
  overflow: hidden;
  
  .ant-card-head {
    background: linear-gradient(135deg, ${NobleColors.primary.commandBlue} 0%, ${NobleColors.primary.commandBlueDark} 100%);
    border: none;
    padding: ${NobleSpacing.lg} ${NobleSpacing.xl};
    color: white;
  }
  
  .ant-card-body {
    padding: ${NobleSpacing.xl};
  }
`;

const JobHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
`;

const JobHeaderLeft = styled.div`
  flex: 1;
`;

const JobNumber = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: ${NobleSpacing.xxs};
  font-family: 'JetBrains Mono', monospace;
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
`;

const JobTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: ${NobleSpacing.xs};
`;

const JobEquipment = styled.div`
  font-size: 13px;
  opacity: 0.85;
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
`;

const StatusBadge = styled(Tag)<{ $status: string }>`
  &&& {
    border: none;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${({ $status }) => {
      switch ($status) {
        case 'completed': return NobleColors.semantic.operationalGreen;
        case 'in-progress': return NobleColors.primary.commandBlue;
        case 'paused': return NobleColors.semantic.warningAmber;
        default: return NobleColors.accent.steelGray;
      }
    }};
    color: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
`;

const PriorityBadge = styled.span<{ $priority: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: ${({ $priority }) => {
    switch ($priority) {
      case 'critical': return NobleColors.semantic.criticalRed;
      case 'urgent': return NobleColors.semantic.warningAmber;
      default: return NobleColors.accent.steelGray;
    }
  }};
  color: white;
  margin-left: ${NobleSpacing.xs};
`;

const TorqueSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${NobleSpacing.xl} 0;
  background: linear-gradient(180deg, ${NobleColors.background.level0Light} 0%, ${NobleColors.background.level1Light} 100%);
  border-radius: 12px;
  margin-bottom: ${NobleSpacing.lg};
`;

const TaskList = styled.div`
  margin-bottom: ${NobleSpacing.lg};
`;

const TaskItem = styled.div<{ $completed: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${NobleSpacing.md};
  padding: ${NobleSpacing.md};
  background: ${({ $completed }) => ($completed ? NobleColors.background.level0Light : 'white')};
  border: 2px solid ${({ $completed }) => ($completed ? NobleColors.semantic.operationalGreen : NobleColors.background.level2Light)};
  border-radius: 8px;
  margin-bottom: ${NobleSpacing.sm};
  transition: all 0.2s ease-out;
  
  &:hover {
    box-shadow: ${NobleShadows['elevation-1']};
    transform: translateY(-1px);
  }
`;

const StepNumber = styled.div<{ $completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $completed }) => ($completed ? NobleColors.semantic.operationalGreen : NobleColors.primary.commandBlue)};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskDescription = styled(Text)<{ $completed: boolean }>`
  &&& {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: ${({ $completed }) => ($completed ? NobleColors.accent.steelGray : NobleColors.accent.steelGrayDark)};
    text-decoration: ${({ $completed }) => ($completed ? 'line-through' : 'none')};
    margin-bottom: ${NobleSpacing.xxs};
  }
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.md};
  font-size: 11px;
  color: ${NobleColors.accent.steelGray};
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${NobleSpacing.md};
  margin-bottom: ${NobleSpacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.sm};
  padding: ${NobleSpacing.sm};
  background: ${NobleColors.background.level0Light};
  border-radius: 8px;
`;

const MetadataIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${NobleColors.primary.commandBlue};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const MetadataContent = styled.div`
  flex: 1;
`;

const MetadataLabel = styled(Text)`
  &&& {
    display: block;
    font-size: 11px;
    color: ${NobleColors.accent.steelGray};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const MetadataValue = styled(Text)`
  &&& {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${NobleColors.accent.steelGrayDark};
  }
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.sm};
  flex-wrap: wrap;
`;

// Helper functions
const formatDuration = (minutes?: number): string => {
  if (!minutes) return '--';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'not-started': return 'Not Started';
    case 'in-progress': return 'In Progress';
    case 'paused': return 'Paused';
    case 'completed': return 'Completed';
    default: return status;
  }
};

const calculateProgress = (tasks: JobTask[]): number => {
  if (tasks.length === 0) return 0;
  const completed = tasks.filter(t => t.completed).length;
  return Math.round((completed / tasks.length) * 100);
};

// Main Component
export const NobleJobCard: React.FC<NobleJobCardProps> = ({
  jobNumber,
  title,
  equipmentId,
  equipmentName,
  status,
  priority,
  torqueValue,
  maxTorque,
  tasks,
  technician,
  location,
  startTime,
  estimatedDuration,
  actualDuration,
  onTaskToggle,
  onPause,
  onResume,
  onComplete,
  onAddPhoto,
  onViewQR,
  className,
}) => {
  const progress = calculateProgress(tasks);
  const isCompleted = status === 'completed';
  const isPaused = status === 'paused';
  const isInProgress = status === 'in-progress';

  return (
    <JobCardContainer
      className={className}
      title={
        <JobHeader>
          <JobHeaderLeft>
            <JobNumber>
              <QrcodeOutlined />
              {jobNumber}
              {priority !== 'normal' && <PriorityBadge $priority={priority}>{priority}</PriorityBadge>}
            </JobNumber>
            <JobTitle>{title}</JobTitle>
            <JobEquipment>
              <ToolOutlined />
              {equipmentId} - {equipmentName}
            </JobEquipment>
          </JobHeaderLeft>
          <div>
            <StatusBadge $status={status}>{getStatusLabel(status)}</StatusBadge>
          </div>
        </JobHeader>
      }
    >
      {/* Torque Gauge */}
      <TorqueSection>
        <TorqueGauge
          value={torqueValue}
          maxTorque={maxTorque}
          size="large"
          animated
        />
      </TorqueSection>

      {/* Progress Bar */}
      <div style={{ marginBottom: NobleSpacing.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: NobleSpacing.xs }}>
          <Text strong>Overall Progress</Text>
          <Text strong style={{ color: NobleColors.primary.commandBlue }}>{progress}%</Text>
        </div>
        <Progress
          percent={progress}
          strokeColor={{
            '0%': NobleColors.primary.commandBlue,
            '100%': NobleColors.semantic.operationalGreen,
          }}
          status={isCompleted ? 'success' : 'active'}
        />
      </div>

      {/* Task Checklist */}
      <TaskList>
        <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: NobleSpacing.md }}>
          Tasks ({tasks.filter(t => t.completed).length}/{tasks.length})
        </Text>
        {tasks.map((task) => (
          <TaskItem key={task.id} $completed={task.completed}>
            <StepNumber $completed={task.completed}>
              {task.completed ? <CheckCircleOutlined /> : task.step}
            </StepNumber>
            <TaskContent>
              <TaskDescription $completed={task.completed}>
                {task.description}
              </TaskDescription>
              <TaskMeta>
                {task.estimatedMinutes && (
                  <span>
                    <ClockCircleOutlined /> Est: {formatDuration(task.estimatedMinutes)}
                  </span>
                )}
                {task.completed && task.actualMinutes && (
                  <span style={{ color: NobleColors.semantic.operationalGreen }}>
                    ✓ Actual: {formatDuration(task.actualMinutes)}
                  </span>
                )}
              </TaskMeta>
            </TaskContent>
            <Checkbox
              checked={task.completed}
              onChange={() => onTaskToggle?.(task.id)}
              disabled={isCompleted}
              style={{ transform: 'scale(1.2)' }}
            />
          </TaskItem>
        ))}
      </TaskList>

      <Divider />

      {/* Metadata */}
      <MetadataGrid>
        {technician && (
          <MetadataItem>
            <MetadataIcon>
              {technician.avatar ? (
                <Avatar src={technician.avatar} size={32} />
              ) : (
                <UserOutlined />
              )}
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Technician</MetadataLabel>
              <MetadataValue>{technician.name}</MetadataValue>
            </MetadataContent>
          </MetadataItem>
        )}

        {location && (
          <MetadataItem>
            <MetadataIcon>
              <EnvironmentOutlined />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Location</MetadataLabel>
              <MetadataValue>{location}</MetadataValue>
            </MetadataContent>
          </MetadataItem>
        )}

        {estimatedDuration && (
          <MetadataItem>
            <MetadataIcon>
              <ClockCircleOutlined />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Estimated</MetadataLabel>
              <MetadataValue>{formatDuration(estimatedDuration)}</MetadataValue>
            </MetadataContent>
          </MetadataItem>
        )}

        {actualDuration && (
          <MetadataItem>
            <MetadataIcon>
              <ClockCircleOutlined />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Actual Duration</MetadataLabel>
              <MetadataValue style={{ color: actualDuration > (estimatedDuration || 0) ? NobleColors.semantic.criticalRed : NobleColors.semantic.operationalGreen }}>
                {formatDuration(actualDuration)}
              </MetadataValue>
            </MetadataContent>
          </MetadataItem>
        )}
      </MetadataGrid>

      {/* Actions */}
      <ActionBar>
        {!isCompleted && (
          <>
            {isPaused ? (
              <NobleButton
                variant="primary"
                icon={<PlayCircleOutlined />}
                onClick={onResume}
              >
                Resume
              </NobleButton>
            ) : isInProgress ? (
              <NobleButton
                variant="secondary"
                icon={<PauseCircleOutlined />}
                onClick={onPause}
              >
                Pause
              </NobleButton>
            ) : null}

            <NobleButton
              variant="secondary"
              icon={<CameraOutlined />}
              onClick={onAddPhoto}
            >
              Add Photo
            </NobleButton>

            {progress === 100 && (
              <NobleButton
                variant="primary"
                icon={<CheckCircleOutlined />}
                onClick={onComplete}
              >
                Complete Job
              </NobleButton>
            )}
          </>
        )}

        {onViewQR && (
          <NobleButton
            variant="ghost"
            icon={<QrcodeOutlined />}
            onClick={onViewQR}
          >
            QR Code
          </NobleButton>
        )}
      </ActionBar>
    </JobCardContainer>
  );
};

